import axios from "axios";
import { isPositiveDecimalString } from "./stablesMoney";
import { applyBearerAuth } from "./authToken";
import { STABLES_API_URL } from "../config";
import { createIdempotencyKey } from "./baasConsoleClient";
import {
  DISPLAY_MARKET_QUOTE_AMOUNT,
  getStablesCreateNetworkLabel,
  isQuotesCarouselAsset,
  isStableMarketAsset,
  STABLE_QUOTES_CAROUSEL_ASSET_IDS,
} from "../Pages/Private/Stables/constants";
import { resolveBaasIconUrl } from "../Pages/Private/BaasConsole/Extrato/baasCdnIcons";

const stablesApi = axios.create({
  baseURL: STABLES_API_URL,
});

/** Axios client for stables — exported so AuthContext can sync JWT defaults (same as main api). */
export const stablesHttpClient = stablesApi;

/** Optional BaaS public origin for non-CDN relative paths only (icons use Front Door). */
const stablesPublicBaseUrl = () => {
  const env =
    (typeof process !== "undefined" && process.env?.REACT_APP_BAAS_PUBLIC_URL?.trim()) || "";
  return env.replace(/\/+$/, "");
};

stablesApi.interceptors.request.use(
  (config) => {
    applyBearerAuth(config);

    const method = String(config.method || "get").toUpperCase();
    if (method === "POST") {
      const headers = config.headers || {};
      const existing =
        headers["X-Idempotency-Key"] ??
        headers["x-idempotency-key"];
      if (!existing) {
        headers["X-Idempotency-Key"] = createIdempotencyKey();
        config.headers = headers;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

const unwrap = (payload) => payload?.data ?? payload;

const pick = (row, snake, camel) => row?.[snake] ?? row?.[camel];

export const parseApiNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

/** Resolves unit BRL price from balance/history rows that may use `rate` or `price`. */
export const resolveStablePrice = (row = {}) =>
  parseApiNumber(row.rate ?? row.price);

/** Resolves FoxBit unit price string from GET /markets/quotes (never derived from amounts). */
export const resolveMarketQuoteUnitPrice = (quote = {}) => {
  const raw = quote.price ?? quote.Price;
  if (raw == null || raw === "") return "";
  return String(raw).trim();
};

export const resolveMarketQuoteNumericPrice = (quote = {}) =>
  parseApiNumber(resolveMarketQuoteUnitPrice(quote));

/** Keeps only Baas stablecoin quotes (excludes legacy mock fiat pairs like usd/eur). */
export const filterStableMarketQuotes = (items = []) =>
  items.filter((item) => isStableMarketAsset(item.asset ?? item.id));

/** Carousel: BRL1, USDC, USDT only (excludes EURC and other assets). */
export const filterQuotesCarouselItems = (items = []) =>
  items.filter((item) => isQuotesCarouselAsset(item.asset ?? item.id));

/** Sorts carousel quotes in fixed display order: BRL1 → USDC → USDT. */
export const orderQuotesCarouselItems = (items = []) => {
  const byAsset = new Map(
    items.map((item) => [String(item.asset ?? item.id).trim().toLowerCase(), item])
  );

  return STABLE_QUOTES_CAROUSEL_ASSET_IDS.map((assetId) => byAsset.get(assetId)).filter(Boolean);
};

/** User-facing message for stables API failures (403, 404 Baas, 5xx, network). */
export const getStablesApiErrorMessage = (err, fallback = "Não foi possível carregar os dados.") => {
  const status = err?.response?.status;
  const data = err?.response?.data ?? {};
  const errorCode = data.error ?? data.code;
  const detail =
    data.detail ??
    data.information ??
    data.message ??
    err?.message;

  if (status === 404 && errorCode === "me_request_failed") {
    return "Não foi possível carregar os saldos: sua conta ainda não está vinculada ao serviço de stablecoins (Baas). Entre em contato com o suporte.";
  }

  if (status === 403) return "Acesso negado ao serviço de cotações. Tente novamente mais tarde.";
  if (status === 503) return detail || "Serviço de mercado temporariamente indisponível.";
  if (status >= 500) return "Erro no servidor. Tente novamente em instantes.";
  return detail || fallback;
};

export const mergeAssetsWithQuoteRates = (assets = [], quoteItems = []) => {
  // quoteItems must come from the same carousel getCachedMarketQuotes cache
  // ({ side: "buy", mode: "amount", amount: DISPLAY_MARKET_QUOTE_AMOUNT }).
  const priceByAsset = new Map(
    quoteItems.map((item) => {
      const assetKey = resolveQuoteAssetKey(item);
      const priceString = resolveMarketQuoteUnitPrice(item);
      return [assetKey, { priceString, priceNumber: parseApiNumber(priceString) }];
    })
  );

  return assets.map((asset) => {
    const assetKey = String(asset.id ?? "").trim().toLowerCase();
    const quotePrice = priceByAsset.get(assetKey);
    const balanceRate = resolveStablePrice(asset);
    const rawRate = quotePrice?.priceNumber > 0
      ? quotePrice.priceNumber
      : (balanceRate > 0 ? balanceRate : 0);
    // Unit cotação — keep 4 decimal places (e.g. 5.1086), never truncate to 2.
    const rate = rawRate > 0 ? Number(rawRate.toFixed(4)) : 0;
    const qty = Number(asset.balance) || 0;

    return {
      ...asset,
      rate,
      priceString: quotePrice?.priceString || asset.priceString,
      // Portfolio BRL = StableBalance qty × carousel unit rate (e.g. 0.193745 × 5.1086 ≈ 0.99).
      balanceBrl: rate > 0 && qty > 0
        ? Number((qty * rate).toFixed(2))
        : 0,
    };
  });
};

/** Dedupes market quote rows by asset id (last write wins). */
export const mergeMarketQuoteItems = (...groups) => {
  const byAsset = new Map();

  for (const group of groups) {
    for (const item of group ?? []) {
      const assetKey = resolveQuoteAssetKey(item);
      if (!assetKey) continue;
      byAsset.set(assetKey, item);
    }
  }

  return [...byAsset.values()];
};

/** Builds a carousel quote row from sparkline when FoxBit quotes omit an asset. */
export const buildQuoteFromSparkline = (assetId, sparklineItem = {}) => {
  const assetKey = String(assetId || "").trim().toLowerCase();
  const values = sparklineItem.values ?? [];
  const price = values.length ? parseApiNumber(values[values.length - 1]) : 0;
  if (!assetKey || price <= 0) return null;

  const changePercent = Number(sparklineItem.changePercent) || calcSparklineChangePercent(values);

  return mapMarketQuote({
    id: assetKey,
    asset: assetKey,
    pair: `${assetKey.toUpperCase()} / BRL`,
    price,
    quote_currency: "BRL",
    change_percent: changePercent,
    positive: changePercent >= 0,
  });
};

/** Maps stable asset balance row to UI camelCase shape. */
export const mapStableAsset = (row = {}) => ({
  id: row.id,
  label: row.label,
  rate: resolveStablePrice(row),
  balance: parseApiNumber(row.balance),
  balanceBrl: parseApiNumber(pick(row, "balance_brl", "balanceBrl")),
});

export const mapStablesBalances = (payload) => {
  const root = unwrap(payload);
  const assets = (root?.assets ?? []).map(mapStableAsset);
  return {
    brlBalance: Number(root?.brl_balance ?? root?.brlBalance) || 0,
    totalStablesBrl: Number(root?.total_stables_brl ?? root?.totalStablesBrl) || 0,
    assets,
  };
};

export const toMarketSymbol = (assetId) => {
  const asset = String(assetId || "").trim().toLowerCase();
  return asset ? `${asset}brl` : "";
};

/** Parses FoxBit/Baas market_symbol (e.g. "brl1brl", "usdcbrl") into asset + pair. */
export const parseMarketSymbol = (marketSymbol) => {
  const symbol = String(marketSymbol || "").trim().toLowerCase();
  if (!symbol) {
    return { asset: "", quoteCurrency: "BRL", pair: "" };
  }

  if (symbol.endsWith("brl") && symbol.length > 3) {
    const asset = symbol.slice(0, -3);
    const quoteCurrency = "BRL";
    return {
      asset,
      quoteCurrency,
      pair: asset ? `${asset.toUpperCase()} / ${quoteCurrency}` : "",
    };
  }

  return { asset: "", quoteCurrency: "BRL", pair: "" };
};

const resolveQuoteAssetKey = (row = {}) => {
  const fromFields = String(row.asset ?? row.id ?? "").trim().toLowerCase();
  if (fromFields) return fromFields;

  const marketSymbol = pick(row, "market_symbol", "marketSymbol");
  return parseMarketSymbol(marketSymbol).asset;
};

const isMarketQuoteRow = (row) => {
  if (!row || typeof row !== "object" || Array.isArray(row)) return false;

  return Boolean(
    pick(row, "market_symbol", "marketSymbol")
    || row.asset
    || row.id
    || row.price != null
    || row.rate != null
    || pick(row, "base_amount", "baseAmount") != null
    || pick(row, "quote_amount", "quoteAmount") != null
  );
};

/** Normalizes GET /markets/quotes payloads: items[], root array, or single FoxBit quote. */
export const resolveMarketQuoteRows = (payload) => {
  const root = unwrap(payload);

  if (Array.isArray(root)) {
    return root.filter(isMarketQuoteRow);
  }

  if (Array.isArray(root?.items)) {
    return root.items.filter((item) => item?.available !== false);
  }

  if (Array.isArray(root?.data)) {
    return root.data.filter(isMarketQuoteRow);
  }

  if (isMarketQuoteRow(root) && !Array.isArray(root.items)) {
    return [root];
  }

  return [];
};

export const calcSparklineChangePercent = (values = []) => {
  if (!values.length) return 0;
  const first = values[0];
  const last = values[values.length - 1];
  return first > 0 ? ((last - first) / first) * 100 : 0;
};

/** Default sparkline window for FoxBit market charts (GET /v1/stables/markets/sparkline). */
export const MARKET_SPARKLINE_DEFAULT_WINDOW = "hour";

/** @deprecated Use MARKET_SPARKLINE_DEFAULT_WINDOW */
export const MARKET_SPARKLINE_PERIOD = MARKET_SPARKLINE_DEFAULT_WINDOW;

const normalizeSparklineTimestamp = (ts) => {
  if (!Number.isFinite(ts) || ts <= 0) return NaN;
  return ts < 1e12 ? ts * 1000 : ts;
};

const readSparklinePoint = (point) => {
  if (Array.isArray(point) && point.length >= 2) {
    return { ts: parseApiNumber(point[0]), price: parseApiNumber(point[1]) };
  }

  if (point && typeof point === "object") {
    const ts = parseApiNumber(
      point.timestamp ?? point.ts ?? point.time ?? point[0]
    );
    const price = parseApiNumber(
      point.price ?? point.value ?? point.close ?? point[1]
    );
    return { ts, price };
  }

  return { ts: NaN, price: 0 };
};

const mapSparklinePoints = (points = []) => {
  const labels = [];
  const values = [];

  for (const point of points) {
    const { ts, price } = readSparklinePoint(point);
    if (!Number.isFinite(price) || price <= 0) continue;

    const normalizedTs = normalizeSparklineTimestamp(ts);
    const date = new Date(normalizedTs);
    labels.push(
      Number.isFinite(normalizedTs) && !Number.isNaN(date.getTime())
        ? date.toLocaleString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })
        : ""
    );
    values.push(price);
  }

  return { labels, values, changePercent: calcSparklineChangePercent(values) };
};

export const mapSparklineItem = (item = {}) => {
  const mapped = mapSparklinePoints(item.points ?? []);
  return {
    marketSymbol: pick(item, "market_symbol", "marketSymbol") ?? "",
    ...mapped,
  };
};

const resolveSparklineDataItems = (payload) => {
  if (Array.isArray(payload)) return payload;

  const root = unwrap(payload);
  if (Array.isArray(root)) return root;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root?.items)) return root.items;

  if (root && typeof root === "object" && (root.points || root.market_symbol || root.marketSymbol)) {
    return [root];
  }

  return [];
};

export const mapMarketSparkline = (payload, assetId) => {
  const items = resolveSparklineDataItems(payload);
  const assetKey = String(assetId || "").trim().toLowerCase();
  const marketSymbol = toMarketSymbol(assetKey);

  const item = items.find((row) => {
    const symbol = String(pick(row, "market_symbol", "marketSymbol") ?? "").toLowerCase();
    if (symbol === marketSymbol) return true;
    return assetKey && symbol.startsWith(assetKey);
  }) ?? items[0];

  const mapped = mapSparklineItem(item);

  const root = unwrap(payload);

  return {
    assetId: assetKey,
    period:
      pick(item, "window", "window")
      ?? pick(item, "period", "period")
      ?? root?.window
      ?? root?.period
      ?? MARKET_SPARKLINE_DEFAULT_WINDOW,
    labels: mapped.labels,
    values: mapped.values,
    changePercent: mapped.changePercent,
    marketSymbol: mapped.marketSymbol,
  };
};

export const mapMarketSparklinesByAsset = (payload) => {
  const items = resolveSparklineDataItems(payload);
  const byAsset = new Map();

  for (const item of items) {
    const symbol = String(pick(item, "market_symbol", "marketSymbol") ?? "").toLowerCase();
    const asset = parseMarketSymbol(symbol).asset;
    if (asset) {
      byAsset.set(asset, mapSparklineItem(item));
    }
  }

  return byAsset;
};

export const mapMarketQuote = (row = {}) => {
  const marketSymbol = pick(row, "market_symbol", "marketSymbol") ?? "";
  const parsedSymbol = parseMarketSymbol(marketSymbol);
  const rawBaseAmount = pick(row, "base_amount", "baseAmount");
  const rawQuoteAmount = pick(row, "quote_amount", "quoteAmount");
  const rawPrice = resolveMarketQuoteUnitPrice(row);
  const baseAmount = rawBaseAmount == null || rawBaseAmount === ""
    ? ""
    : String(rawBaseAmount).trim();
  const quoteAmount = rawQuoteAmount == null || rawQuoteAmount === ""
    ? ""
    : String(rawQuoteAmount).trim();
  const price = rawPrice;
  const asset = String(row.asset ?? row.id ?? parsedSymbol.asset ?? "").trim().toLowerCase();
  const quoteCurrency = pick(row, "quote_currency", "quoteCurrency") ?? parsedSymbol.quoteCurrency ?? "BRL";

  return {
    id: row.id ?? asset,
    asset,
    pair: row.pair ?? parsedSymbol.pair ?? (asset ? `${String(asset).toUpperCase()} / ${quoteCurrency}` : ""),
    iconUrl: resolveBaasIconUrl(pick(row, "icon_url", "iconUrl"), {
      symbol: asset,
      baasBaseUrl: stablesPublicBaseUrl(),
    }),
    side: pick(row, "side", "side") ?? null,
    marketSymbol: marketSymbol || null,
    quoteCurrency,
    price,
    priceNumber: parseApiNumber(price),
    baseAmount,
    quoteAmount,
    changePercent: Number(pick(row, "change_percent", "changePercent")) || 0,
    positive: row.positive ?? Number(pick(row, "change_percent", "changePercent")) >= 0,
  };
};

export const mapPriceHistory = (payload) => {
  const root = unwrap(payload);
  return {
    assetId: pick(root, "asset_id", "assetId"),
    period: root?.period ?? "7d",
    labels: root?.labels ?? [],
    values: (root?.values ?? []).map((v) => Number(v) || 0),
    changePercent: Number(pick(root, "change_percent", "changePercent")) || 0,
  };
};

export const mapFeeConfig = (payload) => {
  const root = unwrap(payload);
  return {
    buy: Number(root?.buy) || 0,
    sell: Number(root?.sell) || 0,
    swap: Number(root?.swap) || 0,
    withdraw: Number(root?.withdraw) || 0,
  };
};

export const mapLimitItem = (row = {}) => {
  const usedCents = Number(pick(row, "used_cents", "usedCents")) || 0;
  const totalCents = Number(pick(row, "total_cents", "totalCents")) || 0;
  const explicitAvailable = pick(row, "available_cents", "availableCents");

  return {
    id: row.id,
    label: row.label,
    description: row.description ?? "",
    usedCents,
    totalCents,
    availableCents: explicitAvailable != null
      ? Math.max(0, Number(explicitAvailable) || 0)
      : Math.max(0, totalCents - usedCents),
    percentUsed: Number(pick(row, "percent_used", "percentUsed")) || 0,
    periodType: pick(row, "period_type", "periodType") || null,
    usage: Array.isArray(row.usage)
      ? row.usage.map((item) => ({
          id: String(pick(item, "transaction_id", "transactionId") ?? item?.id ?? ""),
          completedAt: pick(item, "completed_at", "completedAt") ?? null,
          asset: item?.asset ? String(item.asset) : null,
          usedCents: Number(pick(item, "used_cents", "usedCents")) || 0,
        }))
      : [],
  };
};

export const mapNetworkOption = (row = {}) => {
  const id = row.id;
  const formatted = getStablesCreateNetworkLabel(id);
  const known = Boolean(formatted && formatted.includes("["));
  return {
    id,
    label: known ? formatted : row.label || formatted || id,
  };
};

export const mapWithdrawChain = (row = {}) => {
  const id = row.id;
  const formatted = getStablesCreateNetworkLabel(id);
  const known = Boolean(formatted && formatted.includes("["));
  return {
    id,
    label: known ? formatted : row.label || formatted || id,
    networkFeeStable: Number(pick(row, "network_fee_stable", "networkFeeStable")) || 0,
  };
};

export const mapDepositAddress = (payload) => {
  const root = unwrap(payload);
  return {
    assetId: pick(root, "asset_id", "assetId"),
    networkId: String(pick(root, "network_id", "networkId") ?? "")
      .trim()
      .toLowerCase(),
    networkLabel: pick(root, "network_label", "networkLabel"),
    address: root?.address ?? "",
    hasAddress: root?.has_address ?? root?.hasAddress ?? Boolean(root?.address),
  };
};

export const mapFilterOption = (row = {}) => ({
  id: row.id,
  label: row.label,
  count: Number(row.count ?? row.total) || 0,
});

const EMPTY_HISTORY_TAB_COUNTS = {
  all: 0,
  purchases: 0,
  sales: 0,
  swaps: 0,
  withdrawals: 0,
};

const mapHistoryTabCounts = (root) => {
  const direct = root?.tab_counts ?? root?.tabCounts;
  if (direct && typeof direct === "object") {
    return {
      ...EMPTY_HISTORY_TAB_COUNTS,
      ...Object.fromEntries(
        Object.entries(direct).map(([key, value]) => [key, Number(value) || 0])
      ),
    };
  }

  const tabs = root?.tabs;
  if (Array.isArray(tabs) && tabs.length > 0) {
    const hasCounts = tabs.some((tab) => tab.count != null || tab.total != null);
    if (!hasCounts) return null;

    return tabs.reduce(
      (counts, tab) => ({
        ...counts,
        [tab.id]: Number(tab.count ?? tab.total) || 0,
      }),
      { ...EMPTY_HISTORY_TAB_COUNTS }
    );
  }

  return null;
};

export const mapOperationQuote = (row = {}) => {
  const marketSymbol = pick(row, "market_symbol", "marketSymbol") ?? "";
  const parsedSymbol = parseMarketSymbol(marketSymbol);
  const side = String(pick(row, "side", "side") ?? "").trim().toLowerCase();
  const operation = row.operation ?? (side === "sell" ? "sell" : side === "buy" ? "buy" : side || undefined);
  const baseAmount = parseApiNumber(pick(row, "base_amount", "baseAmount"));
  const quoteAmount = parseApiNumber(pick(row, "quote_amount", "quoteAmount"));
  const unitPrice = resolveMarketQuoteNumericPrice(row) || resolveStablePrice(row);

  const explicitBrl = Number(pick(row, "brl_amount", "brlAmount")) || 0;
  const explicitStable = Number(pick(row, "stable_amount", "stableAmount")) || 0;
  const brlAmount = explicitBrl || (operation === "sell" ? quoteAmount : quoteAmount || explicitBrl);
  const stableAmount = explicitStable || baseAmount;

  const assetId = pick(row, "asset_id", "assetId") ?? parsedSymbol.asset;

  return {
    id: row.id,
    operation,
    expiresAt: pick(row, "expires_at", "expiresAt"),
    expiresInSeconds: Number(pick(row, "expires_in_seconds", "expiresInSeconds")) || 8,
    feePercent: Number(pick(row, "fee_percent", "feePercent")) || 0,
    feeBrl: Number(pick(row, "fee_brl", "feeBrl")) || 0,
    feeStable: Number(pick(row, "fee_stable", "feeStable")) || 0,
    feeAssetLabel: pick(row, "fee_asset_label", "feeAssetLabel"),
    brlAmount,
    stableAmount,
    netBrlAmount: Number(pick(row, "net_brl_amount", "netBrlAmount")) || brlAmount,
    netStableAmount: Number(pick(row, "net_stable_amount", "netStableAmount")) || stableAmount,
    fromAmount: pick(row, "from_amount", "fromAmount"),
    toAmountGross: pick(row, "to_amount_gross", "toAmountGross"),
    netFromAmount: pick(row, "net_from_amount", "netFromAmount"),
    netToAmount: pick(row, "net_to_amount", "netToAmount"),
    fromAssetId: pick(row, "from_asset_id", "fromAssetId"),
    toAssetId: pick(row, "to_asset_id", "toAssetId"),
    fromAssetLabel: pick(row, "from_asset_label", "fromAssetLabel"),
    toAssetLabel: pick(row, "to_asset_label", "toAssetLabel"),
    assetId,
    assetLabel: pick(row, "asset_label", "assetLabel"),
    marketSymbol: marketSymbol || null,
    price: unitPrice,
    rate: parseApiNumber(row.rate) || unitPrice,
    networkFeeStable: Number(pick(row, "network_fee_stable", "networkFeeStable")) || 0,
    networkFeeBrl: Number(pick(row, "network_fee_brl", "networkFeeBrl")) || 0,
    totalFeeStable: Number(pick(row, "total_fee_stable", "totalFeeStable")) || 0,
    chainId: pick(row, "chain_id", "chainId"),
    chain: pick(row, "chain_label", "chainLabel") ?? row.chain,
    destination: row.destination ?? "",
  };
};

/** Maps snake_case history row from mobile-service to UI camelCase shape. */
export const mapStablesHistoryItem = (row = {}) => {
  const historyKind = pick(row, "history_kind", "historyKind");
  let amountStable = Number(pick(row, "amount_stable", "amountStable")) || 0;
  const amountBrl = Number(pick(row, "amount_brl", "amountBrl")) || 0;
  const rate = Number(row.rate) || 0;
  let amountTo = Number(pick(row, "amount_to", "amountTo")) || 0;

  // Heal buy rows where API still echoes BRL as crypto qty (R$1 → 1.000000).
  if (
    String(historyKind || "").toLowerCase() === "purchases"
    && amountBrl > 0
    && rate > 0
    && (amountStable <= 0 || Math.abs(amountStable - amountBrl) <= 0.01)
  ) {
    amountStable = amountBrl / rate;
    if (amountTo <= 0 || Math.abs(amountTo - amountBrl) <= 0.01) {
      amountTo = amountStable;
    }
  }

  return {
    id: row.id,
    historyKind,
    date: row.date,
    status: row.status,
    asset: row.asset,
    from: row.from,
    to: row.to,
    amountStable,
    amountBrl,
    amountFrom: pick(row, "amount_from", "amountFrom"),
    amountTo: amountTo || pick(row, "amount_to", "amountTo"),
    amount: row.amount,
    rate,
    destination: row.destination,
    feeStable: pick(row, "fee_stable", "feeStable"),
    /**
     * When true, UI renders hash/explorer block.
     * Buy/sell/swap: occasional — only when API provides tx_hash.
     * Withdraw: always true; hash filled after status becomes concluída/processada.
     */
    hasOnChainProof: Boolean(pick(row, "has_on_chain_proof", "hasOnChainProof")),
    txHash: pick(row, "tx_hash", "txHash"),
    chain: row.chain,
    explorerUrl: pick(row, "explorer_url", "explorerUrl"),
    receiptUrl: pick(row, "receipt_url", "receiptUrl"),
    paymentMethodId: pick(row, "payment_method_id", "paymentMethodId"),
    paymentMethod: pick(row, "payment_method", "paymentMethod"),
    amountBrlEquivalent: pick(row, "amount_brl_equivalent", "amountBrlEquivalent"),
    networkFeeStable: Number(pick(row, "network_fee_stable", "networkFeeStable")) || 0,
    feePercent: Number(pick(row, "fee_percent", "feePercent")) || 0,
    feeBrl: Number(pick(row, "fee_brl", "feeBrl")) || 0,
  };
};

const extractHistoryItems = (payload) => {
  const root = unwrap(payload);
  const items = root?.items ?? root?.data ?? root;
  return Array.isArray(items) ? items.map(mapStablesHistoryItem) : [];
};

const buildHistoryQueryParams = (params = {}) => {
  const query = {};

  if (params.type) query.type = params.type;
  if (params.page) query.page = params.page;
  if (params.page_size) query.page_size = params.page_size;
  if (params.pageSize) query.page_size = params.pageSize;
  if (params.date_from) query.date_from = params.date_from;
  if (params.dateFrom) query.date_from = params.dateFrom;
  if (params.date_to) query.date_to = params.date_to;
  if (params.dateTo) query.date_to = params.dateTo;
  if (params.amount_min != null) query.amount_min = params.amount_min;
  if (params.amountMin != null) query.amount_min = params.amountMin;
  if (params.amount_max != null) query.amount_max = params.amount_max;
  if (params.amountMax != null) query.amount_max = params.amountMax;

  const currencies = params.currencies;
  if (Array.isArray(currencies) && currencies.length) {
    query.currencies = currencies.join(",");
  } else if (typeof currencies === "string" && currencies) {
    query.currencies = currencies;
  }

  const statuses = params.statuses;
  if (Array.isArray(statuses) && statuses.length) {
    query.statuses = statuses.join(",");
  } else if (typeof statuses === "string" && statuses) {
    query.statuses = statuses;
  }

  const paymentMethods = params.payment_methods ?? params.paymentMethods;
  if (Array.isArray(paymentMethods) && paymentMethods.length) {
    query.payment_methods = paymentMethods.join(",");
  } else if (typeof paymentMethods === "string" && paymentMethods) {
    query.payment_methods = paymentMethods;
  }

  return query;
};

export const getStablesBalances = async () => {
  const { data } = await stablesApi.get("/v1/stables/balances");
  return mapStablesBalances(data);
};

/**
 * Supported networks for an asset from Baas catalog
 * (`GET /v1/stables/assets/{asset}/networks` via MS).
 * @returns {Promise<Array<{ id: string, label: string }>>}
 */
export const getStablesAssetNetworks = async (asset) => {
  const symbol = String(asset || "").trim().toUpperCase();
  if (!symbol) return [];

  const { data } = await stablesApi.get(
    `/v1/stables/assets/${encodeURIComponent(symbol)}/networks`
  );
  const root = unwrap(data);
  const rows = Array.isArray(root?.networks)
    ? root.networks
    : Array.isArray(root?.Networks)
      ? root.Networks
      : Array.isArray(root)
        ? root
        : [];

  return rows
    .map((item) => {
      const id = String(
        pick(item, "id", "network_code", "networkCode", "code") || ""
      )
        .trim()
        .toUpperCase();
      if (!id) return null;
      const label =
        String(pick(item, "label", "network_name", "networkName") || "").trim() ||
        id;
      return { id, label };
    })
    .filter(Boolean);
};

/**
 * Provision a stables wallet on the active IB account
 * (`POST /v1/stables/wallets` → Baas create wallet).
 */
export const createStablesWallet = async ({ asset, network } = {}) => {
  const body = {
    asset: String(asset || "").trim().toUpperCase(),
  };
  if (network) {
    body.network = String(network).trim().toUpperCase();
  }

  const { data } = await stablesApi.post("/v1/stables/wallets", body);
  const root = unwrap(data);
  return {
    walletId: pick(root, "wallet_id", "walletId") || null,
    accountId: pick(root, "account_id", "accountId") || null,
    status: root?.status || null,
    asset: String(root?.asset || body.asset || "").toUpperCase(),
    network: root?.network || body.network || null,
    address: root?.address || null,
    created: Boolean(root?.created),
    balance: root?.balance
      ? {
          balanceId: pick(root.balance, "balance_id", "balanceId") || null,
          asset: String(root.balance.asset || body.asset || "").toUpperCase(),
          available: parseApiNumber(root.balance.available),
          blocked: parseApiNumber(root.balance.blocked),
          future: parseApiNumber(root.balance.future),
        }
      : null,
    raw: root,
  };
};

const mapMarketQuotesResponse = (data) => {
  const root = unwrap(data);
  const rows = resolveMarketQuoteRows(data);
  const firstRow = rows[0] ?? root ?? {};

  return {
    side: root?.side ?? firstRow.side ?? "buy",
    mode: root?.mode ?? "amount",
    quoteCurrency: pick(root, "quote_currency", "quoteCurrency")
      ?? parseMarketSymbol(pick(firstRow, "market_symbol", "marketSymbol")).quoteCurrency
      ?? "BRL",
    amount: Number(root?.amount) || DISPLAY_MARKET_QUOTE_AMOUNT,
    marketSymbol: pick(root, "market_symbol", "marketSymbol")
      ?? pick(firstRow, "market_symbol", "marketSymbol")
      ?? null,
    baseAmount: pick(root, "base_amount", "baseAmount") != null
      ? String(pick(root, "base_amount", "baseAmount"))
      : (pick(firstRow, "base_amount", "baseAmount") != null
        ? String(pick(firstRow, "base_amount", "baseAmount"))
        : ""),
    quoteAmount: pick(root, "quote_amount", "quoteAmount") != null
      ? String(pick(root, "quote_amount", "quoteAmount"))
      : (pick(firstRow, "quote_amount", "quoteAmount") != null
        ? String(pick(firstRow, "quote_amount", "quoteAmount"))
        : ""),
    price: resolveMarketQuoteUnitPrice(root) || resolveMarketQuoteUnitPrice(firstRow),
    items: rows.map(mapMarketQuote),
    fetchedAt: pick(root, "fetched_at", "fetchedAt"),
  };
};

export const findMarketQuoteForAsset = (quotesOrItems = [], assetId) => {
  const assetKey = String(assetId || "").trim().toLowerCase();
  if (!assetKey) return null;

  const isFullResponse = quotesOrItems != null
    && !Array.isArray(quotesOrItems)
    && Array.isArray(quotesOrItems.items);
  const items = isFullResponse ? quotesOrItems.items : quotesOrItems;
  const root = isFullResponse ? quotesOrItems : null;

  const fromItems = (items ?? []).find((item) => {
    const key = resolveQuoteAssetKey(item);
    return key === assetKey;
  });
  if (fromItems) return fromItems;

  if (root && (isPositiveDecimalString(root.price) || root.marketSymbol || root.baseAmount || root.quoteAmount)) {
    const parsed = parseMarketSymbol(root.marketSymbol);
    const rootAsset = parsed.asset || assetKey;

    if (!root.marketSymbol || rootAsset === assetKey) {
      return mapMarketQuote({
        asset: assetKey,
        id: assetKey,
        side: root.side,
        market_symbol: root.marketSymbol ?? toMarketSymbol(assetKey),
        base_amount: root.baseAmount,
        quote_amount: root.quoteAmount,
        price: root.price,
        quote_currency: root.quoteCurrency,
      });
    }
  }

  return null;
};

const fetchMarketQuoteForAsset = async ({ side, mode, amount, asset }) => {
  const { data } = await stablesApi.get("/v1/stables/markets/quotes", {
    params: { side, mode, amount, asset },
  });
  return mapMarketQuotesResponse(data);
};

export const getMarketQuotes = async ({
  side = "buy",
  mode = "amount",
  amount = DISPLAY_MARKET_QUOTE_AMOUNT,
  asset,
} = {}) => {
  const params = { side, mode, amount };
  if (asset) params.asset = asset;

  const { data } = await stablesApi.get("/v1/stables/markets/quotes", { params });
  let result = mapMarketQuotesResponse(data);

  if (!asset) {
    const present = new Set(result.items.map((item) => resolveQuoteAssetKey(item)));
    const missing = STABLE_QUOTES_CAROUSEL_ASSET_IDS.filter((id) => !present.has(id));

    if (missing.length) {
      const { getCachedMarketQuotes } = await import("./marketQuotesCache");
      const extras = await Promise.all(
        missing.map(async (assetId) => {
          try {
            const perAsset = await getCachedMarketQuotes({ side, mode, amount, asset: assetId });
            const selected = findMarketQuoteForAsset(perAsset, assetId);
            return selected ? { ...selected, price: resolveMarketQuoteUnitPrice(selected) } : null;
          } catch {
            return null;
          }
        })
      );

      result = {
        ...result,
        items: mergeMarketQuoteItems(result.items, extras.filter(Boolean)),
      };
    }
  }

  return result;
};

/** Fills missing assets via shared marketQuotesCache (never sparkline for display price). */
export const supplementMissingMarketQuotes = async (
  items = [],
  assetIds = [],
  {
    side = "buy",
    mode = "amount",
    amount = DISPLAY_MARKET_QUOTE_AMOUNT,
  } = {}
) => {
  const normalizedIds = assetIds
    .map((id) => String(id || "").trim().toLowerCase())
    .filter(Boolean);
  if (!normalizedIds.length) return items;

  const present = new Set(
    items.map((item) => String(item.asset ?? item.id ?? "").trim().toLowerCase())
  );
  const missing = normalizedIds.filter((id) => !present.has(id));
  if (!missing.length) return items;

  const { getCachedMarketQuotes } = await import("./marketQuotesCache");

  const extras = await Promise.all(
    missing.map(async (assetId) => {
      try {
        const perAsset = await getCachedMarketQuotes({ side, mode, amount, asset: assetId });
        const selected = findMarketQuoteForAsset(perAsset, assetId);
        return selected ? { ...selected, price: resolveMarketQuoteUnitPrice(selected) } : null;
      } catch {
        return null;
      }
    })
  );

  return mergeMarketQuoteItems(items, extras.filter(Boolean));
};

/** @deprecated Use supplementMissingMarketQuotes — sparkline must not drive display price. */
export const supplementMarketQuotesFromSparkline = supplementMissingMarketQuotes;

export const getMarketSparkline = async ({
  assets,
  marketSymbols,
  window = MARKET_SPARKLINE_DEFAULT_WINDOW,
} = {}) => {
  const params = {};

  if (window) {
    params.window = window;
  }

  if (assets) {
    const raw = Array.isArray(assets) ? assets : String(assets).split(",");
    const stableAssets = raw.map((id) => String(id).trim().toLowerCase()).filter(isStableMarketAsset);
    if (!stableAssets.length) {
      throw new Error("Nenhum ativo stablecoin válido para sparkline.");
    }
    params.assets = stableAssets.join(",");
  }

  if (!params.assets && !params.market_symbols) {
    throw new Error("Parâmetro assets ou market_symbols é obrigatório.");
  }

  if (marketSymbols) {
    const value = Array.isArray(marketSymbols) ? marketSymbols.join(",") : marketSymbols;
    if (value) params.market_symbols = value;
  }

  const { data } = await stablesApi.get("/v1/stables/markets/sparkline", { params });
  return data;
};

export const enrichQuotesWithSparklineChange = (items = [], sparklineByAsset = new Map()) =>
  items.map((item) => {
    const assetKey = String(item.asset ?? item.id ?? "").toLowerCase();
    const sparkline = sparklineByAsset.get(assetKey);
    const apiChange = Number(item.changePercent) || 0;
    const changePercent = apiChange !== 0 ? apiChange : (sparkline?.changePercent ?? 0);
    const price = resolveMarketQuoteUnitPrice(item);

    return {
      ...item,
      price,
      priceNumber: parseApiNumber(price),
      changePercent,
      positive: changePercent >= 0,
    };
  });

/** @deprecated Use getMarketQuotes — kept for legacy imports. */
export const getStablesQuotes = getMarketQuotes;

/** @deprecated Use getMarketSparkline. */
export const getStablesPriceHistory = async (assetId, period = "7d") => {
  const { data } = await stablesApi.get(
    `/v1/stables/assets/${encodeURIComponent(assetId)}/price-history`,
    { params: { period } }
  );
  return mapPriceHistory(data);
};

export const getStablesOperations = async () => {
  const { data } = await stablesApi.get("/v1/stables/operations");
  const root = unwrap(data);
  return root?.items ?? [];
};

export const getStablesFees = async () => {
  const { data } = await stablesApi.get("/v1/stables/fees");
  return mapFeeConfig(data);
};

export const getStablesLimits = async (walletId = null) => {
  const params = walletId ? { wallet_id: walletId } : undefined;
  const { data } = await stablesApi.get("/v1/stables/limits", { params });
  const root = unwrap(data);
  return {
    items: (root?.items ?? []).map(mapLimitItem),
    withdrawOnchain: mapLimitItem(root?.withdraw_onchain ?? root?.withdrawOnchain ?? {}),
    policies: Array.isArray(root?.policies)
      ? root.policies.map((item) => ({
          operationType: item.operation_type ?? item.operationType,
          periodType: item.period_type ?? item.periodType,
          usedCents: Number(item.used_cents ?? item.usedCents ?? 0),
          limitCents: Number(item.limit_cents ?? item.limitCents ?? 0),
          availableCents: Number(item.available_cents ?? item.availableCents ?? 0),
        }))
      : [],
  };
};

/** Exact write-scope policies — GET /v1/stables/limits/policies (IB returns 403; admin uses Baas console). */
export const getStablesLimitPolicies = async (walletId = null) => {
  const params = walletId ? { wallet_id: walletId } : undefined;
  const { data } = await stablesApi.get("/v1/stables/limits/policies", { params });
  const root = unwrap(data);
  const policies = Array.isArray(root?.policies)
    ? root.policies.map((item) => ({
        id: item.id,
        operationType: item.operation_type ?? item.operationType,
        periodType: item.period_type ?? item.periodType,
        limitAmountCents: Number(
          item.limit_amount_cents ?? item.limitAmountCents ?? 0,
        ),
        active: item.active !== false,
      }))
    : [];
  return {
    accountId: root?.account_id ?? root?.accountId ?? null,
    walletId: root?.wallet_id ?? root?.walletId ?? walletId,
    scope: root?.scope ?? "account",
    policies,
    total: Number(root?.total ?? policies.length),
  };
};

/** Soft-replace StableLimitPolicy — blocked on IB (403); Valut admins use Baas console. */
export const setStablesLimits = async (body = {}) => {
  const { data } = await stablesApi.put("/v1/stables/limits", body);
  return unwrap(data);
};

export const getStablesDepositNetworks = async () => {
  const { data } = await stablesApi.get("/v1/stables/deposit/networks");
  const root = unwrap(data);
  return (root?.items ?? []).map(mapNetworkOption);
};

export const getStablesDepositAddress = async (assetId, networkId) => {
  const params = { asset_id: assetId };
  if (networkId) {
    params.network_id = networkId;
  }
  const { data } = await stablesApi.get("/v1/stables/deposit/addresses", { params });
  return mapDepositAddress(data);
};

export const generateStablesDepositAddress = async (assetId, networkId) => {
  const { data } = await stablesApi.post("/v1/stables/deposit/addresses/generate", {
    asset_id: assetId,
    network_id: networkId,
  });
  return mapDepositAddress(data);
};

export const getStablesWithdrawChains = async () => {
  const { data } = await stablesApi.get("/v1/stables/withdraw/chains");
  const root = unwrap(data);
  return (root?.items ?? []).map(mapWithdrawChain);
};

export const getStablesHistoryFilterOptions = async () => {
  const { data } = await stablesApi.get("/v1/stables/history/filter-options");
  const root = unwrap(data);
  return {
    tabs: (root?.tabs ?? []).map(mapFilterOption),
    tabCounts: mapHistoryTabCounts(root),
    currencies: (root?.currencies ?? []).map(mapFilterOption),
    statuses: (root?.statuses ?? []).map(mapFilterOption),
    paymentMethods: (root?.payment_methods ?? root?.paymentMethods ?? []).map(mapFilterOption),
  };
};

/** Paginated history with optional filters (snake_case query params). */
export const getStablesHistory = async (params = {}) => {
  const { data } = await stablesApi.get("/v1/stables/history", {
    params: buildHistoryQueryParams(params),
  });
  const root = unwrap(data);
  const items = extractHistoryItems(root);
  return {
    items,
    total: root?.total ?? items.length,
    page: root?.page ?? params.page ?? 1,
    pageSize: root?.page_size ?? root?.pageSize ?? params.page_size ?? 10,
    totalPages: Math.max(1, root?.total_pages ?? root?.totalPages ?? 1),
  };
};

/** Export filtered history as CSV wrapper. */
export const exportStablesHistory = async (params = {}) => {
  const { data } = await stablesApi.get("/v1/stables/history/export", {
    params: buildHistoryQueryParams(params),
  });
  const root = unwrap(data);
  return {
    filename: root?.filename ?? `historico-stables-${new Date().toISOString().slice(0, 10)}.csv`,
    contentType: root?.content_type ?? root?.contentType ?? "text/csv",
    csvContent: root?.csv_content ?? root?.csvContent ?? "",
  };
};

export const createBuyQuote = async (request) => {
  const { data } = await stablesApi.post("/v1/stables/operations/buy/quote", {
    asset_id: request.assetId ?? request.asset_id,
    input_currency: request.inputCurrency ?? request.input_currency,
    amount: request.amount,
  });
  return mapOperationQuote(unwrap(data));
};

export const createSellQuote = async (request) => {
  const { data } = await stablesApi.post("/v1/stables/operations/sell/quote", {
    asset_id: request.assetId ?? request.asset_id,
    input_currency: request.inputCurrency ?? request.input_currency,
    amount: request.amount,
  });
  return mapOperationQuote(unwrap(data));
};

export const createSwapQuote = async (request) => {
  const { data } = await stablesApi.post("/v1/stables/operations/swap/quote", {
    from_asset_id: request.fromAssetId ?? request.from_asset_id,
    to_asset_id: request.toAssetId ?? request.to_asset_id,
    amount: request.amount,
  });
  return mapOperationQuote(unwrap(data));
};

export const createWithdrawQuote = async (request) => {
  const { data } = await stablesApi.post("/v1/stables/operations/withdraw/quote", {
    asset_id: request.assetId ?? request.asset_id,
    chain_id: request.chainId ?? request.chain_id,
    amount: request.amount,
    destination: request.destination,
  });
  return mapOperationQuote(unwrap(data));
};

const buildConfirmPayload = (request = {}) => {
  const payload = {};

  if (request.quoteId ?? request.quote_id) {
    payload.quote_id = request.quoteId ?? request.quote_id;
  }
  if (request.assetId ?? request.asset_id) {
    payload.asset_id = request.assetId ?? request.asset_id;
  }
  if (request.inputCurrency ?? request.input_currency) {
    payload.input_currency = request.inputCurrency ?? request.input_currency;
  }
  if (request.amount != null) payload.amount = request.amount;
  if (request.fromAssetId ?? request.from_asset_id) {
    payload.from_asset_id = request.fromAssetId ?? request.from_asset_id;
  }
  if (request.toAssetId ?? request.to_asset_id) {
    payload.to_asset_id = request.toAssetId ?? request.to_asset_id;
  }
  if (request.chainId ?? request.chain_id) {
    payload.chain_id = request.chainId ?? request.chain_id;
  }
  if (request.destination) payload.destination = request.destination;

  return payload;
};

/** Picks receipt payload from confirm wrapper, nested receipt, or flat receipt body. */
const extractReceiptPayload = (root) => {
  if (!root || typeof root !== "object") return {};

  const nested = root.receipt ?? root.Receipt;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return nested;
  }

  if (pick(root, "transaction_id", "transactionId") || root.operation) {
    return root;
  }

  return {};
};

const normalizeOperation = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized || undefined;
};

const ON_CHAIN_OPERATIONS = new Set(["withdraw", "deposit"]);

/**
 * Whether comprovante/detail modals should render the on-chain proof section.
 * Withdraw/deposit: when API sets has_on_chain_proof (hash may arrive later).
 * Buy/sell/swap: only when has_on_chain_proof AND tx_hash are both present.
 */
export const shouldShowOnChainProof = ({ operation, hasOnChainProof, txHash } = {}) => {
  if (!hasOnChainProof) return false;
  const op = normalizeOperation(operation);
  if (ON_CHAIN_OPERATIONS.has(op)) return true;
  return Boolean(txHash);
};

const coalesceAmount = (...values) => {
  for (const value of values) {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
  }
  return 0;
};

const coalesceText = (...values) => {
  for (const value of values) {
    if (value != null && String(value).trim() !== "") return value;
  }
  return undefined;
};

/** True when receipt has enough fields to render without a GET refresh. */
export const isOperationReceiptComplete = (receipt) => {
  if (!receipt?.transactionId || !receipt?.operation) return false;

  switch (normalizeOperation(receipt.operation)) {
    case "buy":
      return receipt.brlAmount > 0 || receipt.netStableAmount > 0;
    case "sell":
      return receipt.stableAmount > 0 || receipt.netBrlAmount > 0;
    case "swap":
      return receipt.fromAmount > 0 || receipt.netToAmount > 0;
    case "withdraw":
      return receipt.stableAmount > 0 || receipt.netStableAmount > 0;
    default:
      return false;
  }
};

/** Prefer existing non-empty receipt values; never replace positive amounts with zero. */
export const mergeOperationReceipts = (base = {}, update = {}) => {
  if (!update?.transactionId) return base ?? {};
  if (!base?.transactionId) return update;

  const merged = { ...base };

  for (const [key, value] of Object.entries(update)) {
    if (typeof value === "number") {
      if (value > 0 || merged[key] == null || merged[key] === 0) {
        merged[key] = value;
      }
      continue;
    }

    if (value != null && value !== "") {
      merged[key] = value;
    }
  }

  return merged;
};

/** Maps snake_case operation receipt from confirm/detail API to UI camelCase shape. */
export const mapOperationReceipt = (row = {}) => ({
  transactionId: pick(row, "transaction_id", "transactionId"),
  quoteId: pick(row, "quote_id", "quoteId"),
  operation: normalizeOperation(row.operation),
  status: row.status,
  completedAt: pick(row, "completed_at", "completedAt"),
  date: row.date,
  assetId: pick(row, "asset_id", "assetId"),
  assetLabel: pick(row, "asset_label", "assetLabel"),
  fromAssetId: pick(row, "from_asset_id", "fromAssetId"),
  toAssetId: pick(row, "to_asset_id", "toAssetId"),
  fromAssetLabel: pick(row, "from_asset_label", "fromAssetLabel"),
  toAssetLabel: pick(row, "to_asset_label", "toAssetLabel"),
  brlAmount: Number(pick(row, "brl_amount", "brlAmount")) || 0,
  stableAmount: Number(pick(row, "stable_amount", "stableAmount")) || 0,
  netBrlAmount: Number(pick(row, "net_brl_amount", "netBrlAmount")) || 0,
  netStableAmount: Number(pick(row, "net_stable_amount", "netStableAmount")) || 0,
  fromAmount: Number(pick(row, "from_amount", "fromAmount")) || 0,
  toAmount: Number(pick(row, "to_amount", "toAmount")) || 0,
  netFromAmount: Number(pick(row, "net_from_amount", "netFromAmount")) || 0,
  netToAmount: Number(pick(row, "net_to_amount", "netToAmount")) || 0,
  feePercent: Number(pick(row, "fee_percent", "feePercent")) || 0,
  feeBrl: Number(pick(row, "fee_brl", "feeBrl")) || 0,
  feeStable: Number(pick(row, "fee_stable", "feeStable")) || 0,
  networkFeeStable: Number(pick(row, "network_fee_stable", "networkFeeStable")) || 0,
  totalFeeStable: Number(pick(row, "total_fee_stable", "totalFeeStable")) || 0,
  rate: Number(row.rate) || 0,
  chain: row.chain || null,
  chainId: pick(row, "chain_id", "chainId") || null,
  destination: row.destination ?? "",
  /**
   * When true, UI renders hash/explorer block on comprovante.
   * Buy/sell/swap confirm: false by default (basic receipt only).
   * Withdraw confirm: true immediately; tx_hash/explorer_url when status is concluída.
   */
  hasOnChainProof: Boolean(pick(row, "has_on_chain_proof", "hasOnChainProof")),
  txHash: pick(row, "tx_hash", "txHash") || null,
  explorerUrl: pick(row, "explorer_url", "explorerUrl") || null,
  receiptUrl: pick(row, "receipt_url", "receiptUrl"),
  paymentMethod: pick(row, "payment_method", "paymentMethod"),
});

const mapConfirmResponse = (payload) => {
  const root = unwrap(payload);
  const receiptPayload = extractReceiptPayload(root);
  const receipt = mapOperationReceipt({
    ...receiptPayload,
    transaction_id:
      pick(receiptPayload, "transaction_id", "transactionId")
      ?? pick(root, "transaction_id", "transactionId"),
    status: receiptPayload.status ?? root.status,
    operation: receiptPayload.operation ?? root.operation,
  });

  return {
    transactionId: pick(root, "transaction_id", "transactionId") ?? receipt.transactionId,
    status: root.status ?? receipt.status,
    receipt,
  };
};

const formatReceiptDate = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;

  const pad = (part) => String(part).padStart(2, "0");
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const HISTORY_KIND_TO_OPERATION = {
  purchases: "buy",
  sales: "sell",
  swaps: "swap",
  withdrawals: "withdraw",
};

/** Maps history list/detail row to receipt-like shape for detail modal display. */
export const mapHistoryItemForDetailDisplay = (item = {}) => {
  const historyKind = item.historyKind ?? pick(item, "history_kind");
  const operation = item.operation ?? HISTORY_KIND_TO_OPERATION[historyKind];

  const base = {
    transactionId: item.id ?? item.transactionId,
    operation,
    historyKind,
    status: item.status,
    date: item.date,
    hasOnChainProof: Boolean(item.hasOnChainProof ?? pick(item, "has_on_chain_proof")),
    txHash: item.txHash ?? pick(item, "tx_hash") ?? null,
    chain: item.chain ?? null,
    explorerUrl: item.explorerUrl ?? pick(item, "explorer_url") ?? null,
    receiptUrl: item.receiptUrl ?? pick(item, "receipt_url"),
    paymentMethod: item.paymentMethod ?? pick(item, "payment_method"),
    paymentMethodId: item.paymentMethodId ?? pick(item, "payment_method_id"),
    rate: Number(item.rate) || 0,
    feePercent: Number(item.feePercent ?? pick(item, "fee_percent")) || 0,
    feeBrl: Number(item.feeBrl ?? pick(item, "fee_brl")) || 0,
    feeStable: Number(item.feeStable ?? pick(item, "fee_stable")) || 0,
    networkFeeStable: Number(item.networkFeeStable ?? pick(item, "network_fee_stable")) || 0,
  };

  switch (operation) {
    case "buy": {
      const amountStable =
        Number(item.amountStable ?? pick(item, "amount_stable"))
        || Number(item.amountTo ?? pick(item, "amount_to"))
        || 0;
      const amountBrl = Number(item.amountBrl ?? pick(item, "amount_brl")) || 0;
      return {
        ...base,
        assetId: item.asset ?? item.assetId,
        assetLabel: item.assetLabel ?? pick(item, "asset_label"),
        brlAmount: amountBrl,
        stableAmount: amountStable,
        netStableAmount: amountStable,
        netBrlAmount: amountBrl,
      };
    }
    case "sell":
      return {
        ...base,
        assetId: item.from ?? item.assetId,
        assetLabel: item.assetLabel ?? pick(item, "from_asset_label", "fromAssetLabel"),
        stableAmount: Number(item.amountFrom ?? pick(item, "amount_from")) || 0,
        netBrlAmount: Number(item.amountTo ?? pick(item, "amount_to")) || 0,
      };
    case "swap":
      return {
        ...base,
        fromAssetId: item.from ?? item.fromAssetId,
        toAssetId: item.to ?? item.toAssetId,
        fromAssetLabel:
          item.fromAssetLabel
          ?? pick(item, "from_asset_label", "fromAssetLabel")
          ?? String(item.from || item.fromAssetId || "").toUpperCase(),
        toAssetLabel:
          item.toAssetLabel
          ?? pick(item, "to_asset_label", "toAssetLabel")
          ?? String(item.to || item.toAssetId || "").toUpperCase(),
        fromAmount: Number(item.amountFrom ?? pick(item, "amount_from")) || 0,
        netToAmount: Number(item.amountTo ?? pick(item, "amount_to")) || 0,
      };
    case "withdraw": {
      const amount = Number(item.amount) || 0;
      const feeStable = base.feeStable;
      return {
        ...base,
        assetId: item.asset ?? item.assetId,
        assetLabel: item.assetLabel ?? pick(item, "asset_label"),
        stableAmount: amount,
        netStableAmount: feeStable > 0 ? amount - feeStable - base.networkFeeStable : amount,
        destination: item.destination ?? "",
      };
    }
    default:
      return base;
  }
};

/** Merges history detail, optional operation receipt, and grid list item for modal display. */
export const mergeHistoryDetailForDisplay = (historyItem = {}, receipt = null, listItem = {}) => {
  const fromHistory = mapHistoryItemForDetailDisplay({ ...listItem, ...historyItem });

  if (!receipt?.transactionId) {
    return fromHistory;
  }

  return {
    ...fromHistory,
    ...receipt,
    transactionId: receipt.transactionId ?? fromHistory.transactionId,
    operation: receipt.operation ?? fromHistory.operation,
    historyKind: fromHistory.historyKind,
    status: receipt.status ?? fromHistory.status,
    date: receipt.date ?? fromHistory.date,
    brlAmount: receipt.brlAmount || fromHistory.brlAmount,
    stableAmount: receipt.stableAmount || fromHistory.stableAmount,
    netBrlAmount: receipt.netBrlAmount || fromHistory.netBrlAmount,
    netStableAmount: receipt.netStableAmount || fromHistory.netStableAmount,
    fromAmount: receipt.fromAmount || fromHistory.fromAmount,
    netToAmount: receipt.netToAmount || fromHistory.netToAmount,
    feePercent: receipt.feePercent || fromHistory.feePercent,
    feeBrl: receipt.feeBrl || fromHistory.feeBrl,
    feeStable: receipt.feeStable || fromHistory.feeStable,
    networkFeeStable: receipt.networkFeeStable || fromHistory.networkFeeStable,
    rate: receipt.rate || fromHistory.rate,
    paymentMethod: receipt.paymentMethod ?? fromHistory.paymentMethod,
    hasOnChainProof: receipt.hasOnChainProof ?? fromHistory.hasOnChainProof,
    txHash: receipt.txHash ?? fromHistory.txHash,
    chain: receipt.chain ?? fromHistory.chain,
    explorerUrl: receipt.explorerUrl ?? fromHistory.explorerUrl,
    receiptUrl: receipt.receiptUrl ?? fromHistory.receiptUrl,
    destination: receipt.destination ?? fromHistory.destination,
    assetId: receipt.assetId ?? fromHistory.assetId,
    fromAssetId: receipt.fromAssetId ?? fromHistory.fromAssetId,
    toAssetId: receipt.toAssetId ?? fromHistory.toAssetId,
  };
};

/** Fills missing receipt fields from the quoted operation (confirm fallback). */
export const enrichReceiptFromQuote = (receipt = {}, quote = {}, operation) => {
  const op = normalizeOperation(receipt.operation ?? quote.operation ?? operation);
  const transactionId = coalesceText(receipt.transactionId);

  return {
    ...receipt,
    operation: op,
    transactionId,
    quoteId: coalesceText(receipt.quoteId, quote.id),
    status: coalesceText(receipt.status, quote.status, "Em processamento"),
    date: coalesceText(receipt.date, quote.date, formatReceiptDate()),
    assetId: coalesceText(receipt.assetId, quote.assetId),
    assetLabel: coalesceText(receipt.assetLabel, quote.assetLabel),
    fromAssetId: coalesceText(receipt.fromAssetId, quote.fromAssetId),
    toAssetId: coalesceText(receipt.toAssetId, quote.toAssetId),
    fromAssetLabel: coalesceText(receipt.fromAssetLabel, quote.fromAssetLabel),
    toAssetLabel: coalesceText(receipt.toAssetLabel, quote.toAssetLabel),
    brlAmount: coalesceAmount(receipt.brlAmount, quote.brlAmount),
    stableAmount: coalesceAmount(receipt.stableAmount, quote.stableAmount),
    netBrlAmount: coalesceAmount(receipt.netBrlAmount, quote.netBrlAmount),
    netStableAmount: coalesceAmount(receipt.netStableAmount, quote.netStableAmount),
    fromAmount: coalesceAmount(receipt.fromAmount, quote.fromAmount, quote.netFromAmount),
    netToAmount: coalesceAmount(receipt.netToAmount, quote.netToAmount),
    feePercent: coalesceAmount(receipt.feePercent, quote.feePercent),
    feeBrl: coalesceAmount(receipt.feeBrl, quote.feeBrl),
    feeStable: coalesceAmount(receipt.feeStable, quote.feeStable),
    networkFeeStable: coalesceAmount(receipt.networkFeeStable, quote.networkFeeStable),
    rate: coalesceAmount(receipt.rate, quote.rate, quote.price),
    chain: coalesceText(receipt.chain, quote.chain) ?? null,
    chainId: coalesceText(receipt.chainId, quote.chainId) ?? null,
    destination: coalesceText(receipt.destination, quote.destination) ?? "",
    receiptUrl: coalesceText(receipt.receiptUrl, quote.receiptUrl),
    paymentMethod: coalesceText(receipt.paymentMethod, quote.paymentMethod),
    hasOnChainProof: receipt.hasOnChainProof ?? false,
    txHash: coalesceText(receipt.txHash) ?? null,
    explorerUrl: coalesceText(receipt.explorerUrl) ?? null,
  };
};

export const confirmBuyOperation = async (request) => {
  const { data } = await stablesApi.post(
    "/v1/stables/operations/buy/confirm",
    buildConfirmPayload(request)
  );
  return mapConfirmResponse(data);
};

export const confirmSellOperation = async (request) => {
  const { data } = await stablesApi.post(
    "/v1/stables/operations/sell/confirm",
    buildConfirmPayload(request)
  );
  return mapConfirmResponse(data);
};

export const confirmSwapOperation = async (request) => {
  const { data } = await stablesApi.post(
    "/v1/stables/operations/swap/confirm",
    buildConfirmPayload(request)
  );
  return mapConfirmResponse(data);
};

export const confirmWithdrawOperation = async (request) => {
  const { data } = await stablesApi.post(
    "/v1/stables/operations/withdraw/confirm",
    buildConfirmPayload(request)
  );
  return mapConfirmResponse(data);
};

const CONFIRM_BY_OPERATION = {
  buy: confirmBuyOperation,
  sell: confirmSellOperation,
  swap: confirmSwapOperation,
  withdraw: confirmWithdrawOperation,
};

/** Confirm a quoted stables operation (buy/sell/swap/withdraw). */
export const confirmStablesOperation = async (operation, request) => {
  const confirmFn = CONFIRM_BY_OPERATION[operation];
  if (!confirmFn) throw new Error(`Unsupported operation: ${operation}`);
  return confirmFn(request);
};

/** Build confirm payload from an active quote object. */
export const buildConfirmRequestFromQuote = (quote = {}, extras = {}) => {
  const base = { quoteId: quote.id };

  if (quote.operation === "swap") {
    return {
      ...base,
      fromAssetId: quote.fromAssetId,
      toAssetId: quote.toAssetId,
      amount: quote.fromAmount ?? quote.netFromAmount,
    };
  }

  if (quote.operation === "withdraw") {
    return {
      ...base,
      assetId: quote.assetId,
      chainId: quote.chainId,
      amount: quote.stableAmount,
      destination: quote.destination,
    };
  }

  const inputCurrency = extras.inputCurrency ?? quote.inputCurrency ?? "brl";
  const isStableInput = inputCurrency === "stable";
  const amount = isStableInput ? quote.stableAmount : quote.brlAmount;

  return {
    ...base,
    assetId: quote.assetId,
    inputCurrency,
    amount,
  };
};

/** Receipt detail for comprovante modal (GET after confirm or on demand). */
export const getOperationReceipt = async (transactionId) => {
  const { data } = await stablesApi.get(
    `/v1/stables/operations/${encodeURIComponent(transactionId)}/receipt`
  );
  const root = unwrap(data);
  const mapped = mapOperationReceipt(extractReceiptPayload(root));
  return mapped.transactionId ? mapped : null;
};

/** History detail for modal — merges history GET with operation receipt when available. */
export const getStablesHistoryDetail = async (transactionId, listItem = {}) => {
  const { data } = await stablesApi.get(
    `/v1/stables/history/${encodeURIComponent(transactionId)}`
  );
  const historyItem = mapStablesHistoryItem(unwrap(data));

  let receipt = null;
  try {
    receipt = await getOperationReceipt(transactionId);
  } catch {
    // Mock or legacy history rows may not expose the receipt endpoint.
  }

  return mergeHistoryDetailForDisplay(historyItem, receipt, listItem);
};

export default {
  getStablesBalances,
  getStablesAssetNetworks,
  createStablesWallet,
  getMarketQuotes,
  getMarketSparkline,
  getStablesQuotes,
  getStablesPriceHistory,
  getStablesOperations,
  getStablesFees,
  getStablesLimits,
  getStablesDepositNetworks,
  getStablesDepositAddress,
  generateStablesDepositAddress,
  getStablesWithdrawChains,
  getStablesHistoryFilterOptions,
  getStablesHistory,
  getStablesHistoryDetail,
  exportStablesHistory,
  createBuyQuote,
  createSellQuote,
  createSwapQuote,
  createWithdrawQuote,
  confirmBuyOperation,
  confirmSellOperation,
  confirmSwapOperation,
  confirmWithdrawOperation,
  confirmStablesOperation,
  buildConfirmRequestFromQuote,
  getOperationReceipt,
  mapOperationReceipt,
  enrichReceiptFromQuote,
  isOperationReceiptComplete,
  shouldShowOnChainProof,
  mergeOperationReceipts,
  mapStablesHistoryItem,
  mapHistoryItemForDetailDisplay,
  mergeHistoryDetailForDisplay,
  mapOperationQuote,
  mapStableAsset,
};
